/*
  Master User States (masterUserStates)

  This object contains all the possible states a user can have on WM.com.
  The keys contain the values that AEM Authors can assign to components.
*/
export const masterUserStates = {
  types: {
    journey: {
      label: "Journey",
      states: {
        /*
          Visitor to wm.com who has not entered a street address.
        */
        anonymous: {
          key: "anonymous",
          label: "Anonymous",
        },
        /*
          Visitor to wm.com who has entered a street address that does not
          existing service with WM.
        */
        prospective: {
          key: "prospective",
          label: "Prospective",
        },
        /*
          Visitor to wm.com who has entered a street address that has existing
          service with WM.

          AKA Account Detected.
        */
        existing_user: {
          key: "existing_user",
          label: "Existing User",
        },
        /*
          Visitor to wm.com who is currently logged in to My WM.
        */
        loggedIn: {
          key: "loggedIn",
          label: "Logged In",
        },
        /*
          Visitor to wm.com who has logged in to My WM previously or has
          verified themselves and got access to My WM features as a guest.

          AKA Returning User.
        */
        cookied: {
          key: "cookied",
          label: "Cookied",
        },
      },
      statesList: [
        "anonymous",
        "prospective",
        "existing_user",
        "loggedIn",
        "cookied",
      ],
    },
    lob: {
      label: "Line of Business",
      states: {
        omr: {
          key: "omr",
          label: "Open Market Residential",
        },
        smb: {
          key: "smb",
          label: "Small Medium Business",
        },
        tro: {
          key: "tro",
          label: "Temporary Roll-Off",
        },
      },
      statesList: ["omr", "smb", "tro"],
    },
  },
  typesList: ["journey", "lob"],
}

export const USER_STATES = {
  NEW_USER: `NEW_USER`,
  ACCOUNT_DETECTED: `ACCOUNT_DETECTED`,
  LOGGED_IN: `LOGGED_IN`,
  COOKIED: `COOKIED`,
  ANONYMOUS: `ANONYMOUS`,
  RETURNING: `RETURNING`,
  RETURNING_OMR_AVAILABLE: `RETURNING_OMR_AVAILABLE`,
  RETURNING_SMB_AVAILABLE: `RETURNING_SMB_AVAILABLE`,
  RETURNING_TRO_AVAILABLE: `RETURNING_TRO_AVAILABLE`,
  RETURNING_OMR_TRO: `RETURNING_OMR_TRO`,
  RETURNING_SMB_TRO: `RETURNING_SMB_TRO`,
  RETURNING_ALL_AVAILABLE: `RETURNING_ALL_AVAILABLE`,
  LOGGED_IN_OMR_AVAILABLE: `LOGGED_IN_OMR_AVAILABLE`,
  LOGGED_IN_SMB_AVAILABLE: `LOGGED_IN_SMB_AVAILABLE`,
  LOGGED_IN_TRO_AVAILABLE: `LOGGED_IN_TRO_AVAILABLE`,
  LOGGED_IN_OMR_TRO: `LOGGED_IN_OMR_TRO`,
  LOGGED_IN_SMB_TRO: `LOGGED_IN_SMB_TRO`,
  LOGGED_IN_ALL_AVAILABLE: `LOGGED_IN_ALL_AVAILABLE`,
  ACCOUNT_DETECTED_OMR_AVAILABLE: `ACCOUNT_DETECTED_OMR_AVAILABLE`,
  ACCOUNT_DETECTED_SMB_AVAILABLE: `ACCOUNT_DETECTED_SMB_AVAILABLE`,
  ACCOUNT_DETECTED_TRO_AVAILABLE: `ACCOUNT_DETECTED_TRO_AVAILABLE`,
  ACCOUNT_DETECTED_OMR_TRO: `ACCOUNT_DETECTED_OMR_TRO`,
  ACCOUNT_DETECTED_SMB_TRO: `ACCOUNT_DETECTED_SMB_TRO`,
  ACCOUNT_DETECTED_ALL_AVAILABLE: `ACCOUNT_DETECTED_ALL_AVAILABLE`,
}

export const LOB_USER_STATES = {
  RETURNING_OMR_AVAILABLE: true,
  RETURNING_SMB_AVAILABLE: true,
  RETURNING_TRO_AVAILABLE: true,
  RETURNING_OMR_TRO: true,
  RETURNING_SMB_TRO: true,
  RETURNING_ALL_AVAILABLE: true,
  LOGGED_IN_OMR_AVAILABLE: true,
  LOGGED_IN_SMB_AVAILABLE: true,
  LOGGED_IN_TRO_AVAILABLE: true,
  LOGGED_IN_OMR_TRO: true,
  LOGGED_IN_SMB_TRO: true,
  LOGGED_IN_ALL_AVAILABLE: true,
}

export const EXISTING_USER_LOB_STATES = {
  RETURNING_OMR_AVAILABLE: true,
  RETURNING_SMB_AVAILABLE: true,
  RETURNING_TRO_AVAILABLE: true,
  RETURNING_OMR_TRO: true,
  RETURNING_SMB_TRO: true,
  RETURNING_ALL_AVAILABLE: true,
  LOGGED_IN_OMR_AVAILABLE: true,
  LOGGED_IN_SMB_AVAILABLE: true,
  LOGGED_IN_TRO_AVAILABLE: true,
  LOGGED_IN_OMR_TRO: true,
  LOGGED_IN_SMB_TRO: true,
  LOGGED_IN_ALL_AVAILABLE: true,
  ACCOUNT_DETECTED_OMR_AVAILABLE: true,
  ACCOUNT_DETECTED_SMB_AVAILABLE: true,
  ACCOUNT_DETECTED_TRO_AVAILABLE: true,
  ACCOUNT_DETECTED_OMR_TRO: true,
  ACCOUNT_DETECTED_SMB_TRO: true,
  ACCOUNT_DETECTED_ALL_AVAILABLE: true,
}

export const AEM_USER_STATES = {
  NEW_USER: [`anonymous`, `prospective`], // User has not logged in and no address entered before. Anonymous/Prospective combined for new AddressBarHero
  NEW_USER1: [`anonymous`], // User has not logged in and no address entered before. Anonymous/Prospective combined for new AddressBarHero
  PROSPECTIVE: [`prospective`],
  ACCOUNT_DETECTED: [`existing`], // Logged out user that now has their address cookied from their login account
  LOGGED_IN: [`loggedIn`], // User who is currently logged in
  // User with cookied address AND the address is serviced.
  COOKIED: [`cookied`], // user has logged in, but has not linked any accounts
  RETURNING_OMR_AVAILABLE: [`cookied`, `omr`], // cookied address with only OMR available
  RETURNING_SMB_AVAILABLE: [`cookied`, `smb`], // cookied address with only SMB available
  RETURNING_TRO_AVAILABLE: [`cookied`, `tro`], // cookied address with only SMB available
  RETURNING_OMR_TRO: [`cookied`, `omr`, `tro`], // cookied address with OMR & TRO available
  RETURNING_SMB_TRO: [`cookied`, `smb`, `tro`], // cookied address with SMB & TRO available
  RETURNING_ALL_AVAILABLE: [`cookied`, `omr`, `smb`, `tro`], // cookied address with OMR, SMB, & TRO available
  LOGGED_IN_OMR_AVAILABLE: [`loggedIn`, `omr`], // logged in user with only OMR available
  LOGGED_IN_SMB_AVAILABLE: [`loggedIn`, `smb`], // logged in user with only SMB available
  LOGGED_IN_TRO_AVAILABLE: [`loggedIn`, `tro`], // logged in user with only TRO available
  LOGGED_IN_OMR_TRO: [`loggedIn`, `omr`, `tro`], // logged in user with OMR & TRO available
  LOGGED_IN_SMB_TRO: [`loggedIn`, `smb`, `tro`], // logged in user with SMB & TRO available
  LOGGED_IN_ALL_AVAILABLE: [`loggedIn`, `omr`, `smb`, `tro`], // logged in user with OMR, SMB, & TRO available
  ACCOUNT_DETECTED_OMR_AVAILABLE: [`existing`, `omr`], // existing user with only OMR available
  ACCOUNT_DETECTED_SMB_AVAILABLE: [`existing`, `smb`], // existing user with only SMB available
  ACCOUNT_DETECTED_TRO_AVAILABLE: [`existing`, `tro`], // existing user with only TRO available
  ACCOUNT_DETECTED_OMR_TRO: [`existing`, `omr`, `tro`], // existing user with OMR & TRO available
  ACCOUNT_DETECTED_SMB_TRO: [`existing`, `smb`, `tro`], // existing user with SMB & TRO available
  ACCOUNT_DETECTED_ALL_AVAILABLE: [`existing`, `omr`, `smb`, `tro`], // existing user with OMR, SMB, & TRO available
}

export const USER_STATE_TO_AEM_STATE_MAPPING = {
  [AEM_USER_STATES.NEW_USER]: USER_STATES.NEW_USER,
  [AEM_USER_STATES.NEW_USER1]: USER_STATES.NEW_USER,
  [AEM_USER_STATES.NEW_USER2]: USER_STATES.NEW_USER,
  [AEM_USER_STATES.NEW_USER3]: USER_STATES.NEW_USER,
  [AEM_USER_STATES.NEW_USER4]: USER_STATES.NEW_USER,
  [AEM_USER_STATES.COOKIED]: USER_STATES.COOKIED,
  [AEM_USER_STATES.ACCOUNT_DETECTED]: USER_STATES.ACCOUNT_DETECTED,
  [AEM_USER_STATES.LOGGED_IN]: USER_STATES.LOGGED_IN,
  [AEM_USER_STATES.RETURNING_OMR_AVAILABLE]:
    USER_STATES.RETURNING_OMR_AVAILABLE,
  [AEM_USER_STATES.RETURNING_SMB_AVAILABLE]:
    USER_STATES.RETURNING_SMB_AVAILABLE,
  [AEM_USER_STATES.RETURNING_TRO_AVAILABLE]:
    USER_STATES.RETURNING_TRO_AVAILABLE,
  [AEM_USER_STATES.RETURNING_OMR_TRO]: USER_STATES.RETURNING_OMR_TRO,
  [AEM_USER_STATES.RETURNING_SMB_TRO]: USER_STATES.RETURNING_SMB_TRO,
  [AEM_USER_STATES.RETURNING_ALL_AVAILABLE]:
    USER_STATES.RETURNING_ALL_AVAILABLE,
  [AEM_USER_STATES.LOGGED_IN_OMR_AVAILABLE]:
    USER_STATES.LOGGED_IN_OMR_AVAILABLE,
  [AEM_USER_STATES.LOGGED_IN_SMB_AVAILABLE]:
    USER_STATES.LOGGED_IN_SMB_AVAILABLE,
  [AEM_USER_STATES.LOGGED_IN_TRO_AVAILABLE]:
    USER_STATES.LOGGED_IN_TRO_AVAILABLE,
  [AEM_USER_STATES.LOGGED_IN_OMR_TRO]: USER_STATES.LOGGED_IN_OMR_TRO,
  [AEM_USER_STATES.LOGGED_IN_SMB_TRO]: USER_STATES.LOGGED_IN_SMB_TRO,
  [AEM_USER_STATES.LOGGED_IN_ALL_AVAILABLE]:
    USER_STATES.LOGGED_IN_ALL_AVAILABLE,
  [AEM_USER_STATES.ACCOUNT_DETECTED_OMR_AVAILABLE]:
    USER_STATES.ACCOUNT_DETECTED_OMR_AVAILABLE,
  [AEM_USER_STATES.ACCOUNT_DETECTED_SMB_AVAILABLE]:
    USER_STATES.ACCOUNT_DETECTED_SMB_AVAILABLE,
  [AEM_USER_STATES.ACCOUNT_DETECTED_TRO_AVAILABLE]:
    USER_STATES.ACCOUNT_DETECTED_TRO_AVAILABLE,
  [AEM_USER_STATES.ACCOUNT_DETECTED_OMR_TRO]:
    USER_STATES.ACCOUNT_DETECTED_OMR_TRO,
  [AEM_USER_STATES.ACCOUNT_DETECTED_SMB_TRO]:
    USER_STATES.ACCOUNT_DETECTED_SMB_TRO,
  [AEM_USER_STATES.ACCOUNT_DETECTED_ALL_AVAILABLE]:
    USER_STATES.ACCOUNT_DETECTED_ALL_AVAILABLE,
}

export const COMPONENT_PATH_MAP = {
  ADDRESS_BAR_HERO: "wm/components/content/addressBarHero",
  FEATURED_CONTENT_CARDS: "wm/components/content/featured-card",
  ENTRY_AND_CONTACT: "wm/components/content/entryandcontact",
}

export const COMPONENTS_WITH_LOBS_MAP = {
  [COMPONENT_PATH_MAP.ADDRESS_BAR_HERO]: true,
  [COMPONENT_PATH_MAP.FEATURED_CONTENT_CARDS]: true,
  [COMPONENT_PATH_MAP.ENTRY_AND_CONTACT]: true,
}
